import type { MediaDto } from '@studio/shared';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { errorMessage, uploadImage } from '@/lib/api';
import { cn } from '@/lib/utils';

/** Up to `max` pictures: shows them, removes them, uploads new ones. */
export function ImageSlots({
  images,
  max,
  onChange,
  className,
  size = 'md',
}: {
  images: MediaDto[];
  max: number;
  onChange: (images: MediaDto[]) => void;
  className?: string;
  size?: 'sm' | 'md';
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const box = size === 'sm' ? 'size-20' : 'h-36 w-24';
  const pick = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const added: MediaDto[] = [];
      for (const f of [...files].slice(0, max - images.length)) added.push(await uploadImage(f));
      onChange([...images, ...added]);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };
  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {images.map((m, i) => (
        <div key={m.id} className={cn('group relative overflow-hidden rounded-md border bg-muted', box)}>
          <img src={m.url} alt="" className="size-full object-cover" />
          <button
            type="button"
            className="absolute top-1 right-1 rounded-full bg-black/60 p-0.5 text-white opacity-80 hover:opacity-100"
            onClick={() => onChange(images.filter((_, j) => j !== i))}
            aria-label="Remove picture"
          >
            <X className="size-3.5" />
          </button>
        </div>
      ))}
      {images.length < max && (
        <button
          type="button"
          className={cn(
            'grid place-items-center rounded-md border border-dashed text-muted-foreground hover:bg-accent',
            box,
          )}
          onClick={() => input.current?.click()}
          disabled={busy}
        >
          {busy ? <Loader2 className="size-5 animate-spin" /> : <ImagePlus className="size-5" />}
          <span className="sr-only">Add picture</span>
        </button>
      )}
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        multiple={max > 1}
        hidden
        onChange={(e) => void pick(e.target.files)}
      />
    </div>
  );
}
